import ReactDocumentTitle from "../../components/ReactDocumentTitle";
import Layout, {Header, Main} from "../../components/Layout";
import Container from "../../ui/container";
import classes from "./anonymous-sessions.module.css";
import Breadcrumbs from "../../ui/breadcrumbs";
import ReactLink from "../../components/ReactLink";
import {reverse} from "../../routing";
import React, {useCallback, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useQuery} from "@apollo/client";
import AnonymousSessionsTable from "../../components/account/anonymous-sessions-table/AnonymousSessionsTable";
import {anonymousSessionsQuery, QueryData} from "./anonymous-sessions.graphql";
import Paginator from "../../ui/paginator";
import {TABLE_ROWS_PER_PAGE} from "../../settings";
import {AnonymousResult} from "../../schema";
import FullscreenVideo from "../../ui/fullscreenvideo";

export default function AnonymousSessionsScene() {
  const {t} = useTranslation();

  const [currentPage, setCurrentPage] = useState(1);
  const [sessions, setSessions] = useState<AnonymousResult[]>([])
  const [videoSrc, setVideoSrc] = useState<string>()

  const onPlayButtonClick = useCallback((e: React.MouseEvent) => {
    const src = e.currentTarget.getAttribute("data-id");
    setVideoSrc(src ?? undefined)
  }, [])
  const resetVideoSrc = useCallback(() => setVideoSrc(undefined), [])

  const {data, loading: fetching} = useQuery<QueryData>(anonymousSessionsQuery, {
    variables: {
      offset: (currentPage - 1) * TABLE_ROWS_PER_PAGE,
      limit: TABLE_ROWS_PER_PAGE
    }
  });
  const paginatedList = data?.paginatedList;

  const loading = !data && fetching;

  useEffect(() => {
    if (!loading) {
      setSessions(paginatedList?.items ?? [])
    }
  }, [loading, paginatedList])


  const itemsCount = Math.min(sessions.length, TABLE_ROWS_PER_PAGE);
  const totalItems = paginatedList?.countItems ?? 0;

  const totalPages = Math.ceil(totalItems / TABLE_ROWS_PER_PAGE);

  return (
    <>
      <ReactDocumentTitle title={t("anonymousSessions.title")}/>

      <Layout>
        <Header/>
        <Main>
          <Container className={classes.main}>
            <Breadcrumbs className={classes.breadcrumbs}>
              <ReactLink href={reverse("accountMembers")}>
                {t("breadcrumbs.members")}
              </ReactLink>
              <span> {t("anonymousSessions.sessions")}</span>
            </Breadcrumbs>
            <div className={classes.header}>
              <h1 className={classes.h1}>{t("anonymousSessions.sessions")}</h1>
            </div>

            {!loading && sessions && (
              sessions.length > 0 ? (
                <AnonymousSessionsTable
                  sessions={sessions}
                  onPlayButtonClick={onPlayButtonClick}
                />
              ) : (
                <div className={classes.emptyState}>{t("anonymousSessions.emptyState")}</div>
              )
            )}
            <div className={classes.paginatorRow}>
              <div className={classes.paginatorHelpText}>
                {t("common.paginatorShowingItems", {
                  items: itemsCount,
                  totalItems
                })}
              </div>

              {(totalPages > 1) && (
                <Paginator
                  className={classes.paginator}
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </div>
            {videoSrc && <FullscreenVideo onClose={resetVideoSrc} src={videoSrc}/>}
          </Container>
        </Main>
      </Layout>
    </>
  )
}
